import React, { useState, useRef } from "react";
import {
  Form,
  Button,
  Container,
  Row,
  Col,
  Modal,
  Table,
} from "react-bootstrap";
import { toast } from "react-toastify";
import api from "../api";
import { useNavigate } from "react-router-dom";

const Uploads = ({ onUploadSuccess }) => {
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const fileInputRef = useRef(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [validationReport, setValidationReport] = useState(null);
  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      const fileName = selectedFile.name.toLowerCase();
      if (!fileName.endsWith(".zip")) {
        toast.error("Only .zip files are allowed!");
        e.target.value = "";
        return;
      }
      setFile(selectedFile);
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!file) {
      toast.warning("Please select a ZIP file to upload.");
      return;
    }
    if (!file.name.toLowerCase().endsWith(".zip")) {
      toast.error("Invalid file format. Only ZIP files are allowed.");
      return;
    }

    setIsUploading(true);
    const formData = new FormData();
    formData.append("zipfile", file);

    try {
      const res = await api.post("/uploads", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      const data = res.data;
      console.log("DAta:", data);
      setUploadResult(data);

      if (res.data.success) {
        // toast.success("Upload successful!");
        setShowSuccessModal(true);
        onUploadSuccess?.();
      }

      // Reset input
      setFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
    } catch (err) {
      if (err.response?.data?.validationReport) {
        const data = err.response.data;
        setValidationReport(data.validationReport);
        setShowErrorModal(true);
      } else {
        console.error("Upload error:", err);
      }
    } finally {
      setIsUploading(false);
      setFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleModalClose = () => {
    setShowSuccessModal(false);
    setShowErrorModal(false);
  };
  return (
    <>
      {/* <h4>Upload reports</h4> */}
      <div className="card hptCard mt-4 border-0">
        <div className="card-body">
          <Form onSubmit={handleUpload}>
            <Form.Group controlId="formFile" className="mb-3">
              <Form.Label>
                <i className="bi bi-upload me-2"></i>Upload File
              </Form.Label>
              <Form.Control
                type="file"
                accept=".zip"
                onChange={handleFileChange}
                ref={fileInputRef}
              />
            </Form.Group>
            <Button type="submit" variant="success" disabled={isUploading}>
              {isUploading ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm me-2"
                    role="status"
                    aria-hidden="true"
                  ></span>
                  Uploading...
                </>
              ) : (
                <>
                  <i className="bi bi-cloud-arrow-up me-2"></i>
                  Upload
                </>
              )}
            </Button>
          </Form>
        </div>
      </div>

      <Modal
        show={showSuccessModal}
        onHide={handleModalClose}
        centered
        size="lg"
        className="modal-dialog-scrollable"
      >
        <Modal.Header
          closeButton
          className="bg-success text-white border-0 py-3 rounded-top"
        >
          <Modal.Title className="fw-bold">
            <i className="bi bi-check-circle-fill me-2"></i>
            Upload Completed
          </Modal.Title>
        </Modal.Header>

        <Modal.Body
          className="bg-light"
          style={{
            maxHeight: "70vh", // ✅ Limit body height
            overflowY: "auto", // ✅ Enable vertical scroll
          }}
        >
          <div className="p-3 bg-white rounded shadow-sm">
            <p className="fs-5 fw-semibold text-success mb-3">
              {uploadResult?.message}
            </p>

            <div className="d-flex gap-4 mb-4">
              <div>
                <span className="fw-bold text-dark">Total Processed:</span>{" "}
                <span className="badge bg-success">
                  {uploadResult?.validationReport.totalFiles}
                </span>
              </div>
              <div>
                <span className="fw-bold text-dark">Uploaded:</span>{" "}
                <span className="badge bg-success">
                  {uploadResult?.processedFiles.length || 0}
                </span>
              </div>
              <div>
                <span className="fw-bold text-dark">Duplicates:</span>{" "}
                <span className="badge bg-warning text-dark">
                  {uploadResult?.duplicateFiles.length || 0}
                </span>
              </div>
              <div>
                <span className="fw-bold text-dark">Failed:</span>{" "}
                <span className="badge bg-danger">
                  {uploadResult?.failedFiles?.length || 0}
                </span>
              </div>
              <div>
                <span className="fw-bold text-dark">Invalid Files:</span>{" "}
                <span className="badge bg-danger">
                  {uploadResult?.validationReport.invalidFiles}
                </span>
              </div>
            </div>

            {uploadResult?.processedFiles?.length > 0 && (
              <>
                <h6 className="text-success border-bottom pb-1">
                  ✅ Processed Files
                </h6>
                <ul className="list-group list-group-flush mb-3">
                  {uploadResult.processedFiles.map((f, i) => (
                    <li key={i} className="list-group-item border-0 ps-3">
                      <i className="bi bi-file-earmark-check me-2 text-success"></i>
                      {f}
                    </li>
                  ))}
                </ul>
              </>
            )}

            {uploadResult?.duplicateFiles?.length > 0 && (
              <>
                <h6 className="text-warning border-bottom pb-1">
                  ⚠️ Duplicates Skipped
                </h6>
                <ul className="list-group list-group-flush">
                  {uploadResult.duplicateFiles.map((dup, i) => (
                    <li key={i} className="list-group-item border-0 ps-3">
                      <i className="bi bi-files me-2 text-warning"></i>
                      <strong>{dup.newFile}</strong>

                      <small className="text-muted">
                        Existing File: {dup.existingFile}
                      </small>
                    </li>
                  ))}
                </ul>
              </>
            )}
            {uploadResult?.failedFiles?.length > 0 && (
              <>
                <h6 className="text-danger border-bottom pb-1">
                  ❌ Failed Files
                </h6>
                <div className="table-responsive">
                  <table className="table table-sm table-bordered align-middle">
                    <thead className="table-danger">
                      <tr>
                        <th style={{ width: "40%" }}>File Name</th>
                        <th>Errors</th>
                      </tr>
                    </thead>
                    <tbody>
                      {uploadResult.failedFiles.map((file, idx) => (
                        <tr key={idx}>
                          <td>
                            <i className="bi bi-file-earmark-excel text-danger me-2"></i>
                            {file.fileName}
                          </td>
                          <td className="text-muted small">{file.error}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}

            {uploadResult?.validationReport?.details?.some(
              (d) => !d.isValid
            ) && (
              <>
                <h6 className="text-danger border-bottom pb-1">
                  🚫 Invalid Files (Validation Errors)
                </h6>
                <div className="table-responsive">
                  <table className="table table-sm table-bordered align-middle">
                    <thead className="table-warning">
                      <tr>
                        <th style={{ width: "35%" }}>File Name</th>
                        <th>Errors</th>
                      </tr>
                    </thead>
                    <tbody>
                      {uploadResult.validationReport.details
                        .filter((file) => !file.isValid)
                        .map((file, idx) => (
                          <tr key={idx}>
                            <td>
                              <i className="bi bi-exclamation-triangle text-warning me-2"></i>
                              {file.fileName}
                            </td>
                            <td>
                              {file.errors?.length > 0 ? (
                                <ul className="mb-0 ps-3 text-danger small">
                                  {file.errors.map((err, i) => (
                                    <li key={i}>❌ {err}</li>
                                  ))}
                                </ul>
                              ) : (
                                <span className="text-muted">
                                  No specific errors
                                </span>
                              )}
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}

            {/* If everything is processed successfully */}
            {uploadResult?.totalFailed === 0 &&
              uploadResult?.totalDuplicates === 0 && (
                <div className="alert alert-success mt-3 mb-0" role="alert">
                  🎉 All files uploaded and processed successfully!
                </div>
              )}
          </div>
        </Modal.Body>

        <Modal.Footer className="bg-light border-0">
          <Button
            variant="success"
            onClick={handleModalClose}
            className="px-4 fw-semibold shadow-sm"
          >
            OK
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showErrorModal}
        onHide={handleModalClose}
        size="lg"
        centered
        className="modal-dialog-scrollable"
      >
        <Modal.Header
          closeButton
          className="bg-danger text-white border-0 py-3 rounded-top"
        >
          <Modal.Title className="fw-bold">
            <i className="bi bi-exclamation-triangle-fill me-2"></i>
            Validation Report
          </Modal.Title>
        </Modal.Header>

        <Modal.Body
          className="bg-light"
          style={{
            maxHeight: "70vh", // ✅ Limit body height
            overflowY: "auto", // ✅ Enable vertical scroll
          }}
        >
          {validationReport ? (
            <div className="p-3 bg-white rounded shadow-sm">
              <p className="fs-5 fw-semibold text-danger mb-2">
                The uploaded files contain invalid data!
              </p>
              <p className="text-muted mb-4">Please review and re-upload.</p>

              <div className="d-flex gap-4 mb-4">
                <div>
                  <span className="fw-bold text-dark">Total Files:</span>{" "}
                  <span className="badge bg-secondary">
                    {validationReport.totalFiles}
                  </span>
                </div>
                <div>
                  <span className="fw-bold text-dark">Valid:</span>{" "}
                  <span className="badge bg-success">
                    {validationReport.validFiles}
                  </span>
                </div>
                <div>
                  <span className="fw-bold text-dark">Invalid:</span>{" "}
                  <span className="badge bg-danger">
                    {validationReport.invalidFiles}
                  </span>
                </div>
              </div>

              <div className="table-responsive">
                <table className="table table-bordered align-middle mb-0">
                  <thead className="table-danger">
                    <tr>
                      <th>File Name</th>
                      <th>Errors</th>
                    </tr>
                  </thead>
                  <tbody>
                    {validationReport.details.map((item, idx) => (
                      <tr key={idx}>
                        <td className="fw-semibold">{item.fileName}</td>
                        <td>
                          {item.errors.length > 0 ? (
                            <ul className="text-danger mb-0">
                              {item.errors.map((err, i) => (
                                <li key={i}>
                                  <i className="bi bi-x-circle-fill me-2 text-danger"></i>
                                  {err}
                                </li>
                              ))}
                            </ul>
                          ) : (
                            "-"
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <p>No validation data available.</p>
          )}
        </Modal.Body>

        <Modal.Footer className="bg-light border-0">
          <Button
            variant="danger"
            onClick={handleModalClose}
            className="px-4 fw-semibold shadow-sm"
          >
            Reupload
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default Uploads;
